import React from "react";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PlaceIcon from "@mui/icons-material/Place";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import { fmt, rangeLabel, tagLabel, ROLE, ARC_ROLE, weightColors, highlight, countLineMatches } from "./sceneModel";
import { sceneThumb } from "./sceneThumbs";
import TileImg from "./TileImg";
import { pressable } from "../../../app/a11y";

/** text with search matches marked */
export function Hl({ text, q }) {
  return highlight(text, q).map((s, k) =>
    s.hit ? (
      <span key={k} className="sc-hl">
        {s.t}
      </span>
    ) : (
      <React.Fragment key={k}>{s.t}</React.Fragment>
    )
  );
}

const matchLabel = (t, n) => (n === 1 ? t("1 match") : t("{{count}} matches", { count: n }));

// scene thumbnail: its tile of the scene sprite sheet (else a video frame just after it starts), loaded once it scrolls into view
export class Thumb extends React.Component {
  state = { thumb: null };
  ref = React.createRef();

  componentDidMount() {
    // StrictMode (dev) unmounts and remounts the same instance
    this.unmounted = false;
    if (!window.IntersectionObserver) return this.load();
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      },
      { rootMargin: "200px" }
    );
    this.observer.observe(this.ref.current);
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.observer && this.observer.disconnect();
  }

  load() {
    const { projectId, folder, x } = this.props;
    if (!projectId) return;
    sceneThumb(folder, projectId, x).then((thumb) => thumb && !this.unmounted && this.setState({ thumb }));
  }

  render() {
    const { t, x, large, onPlay } = this.props;
    const { thumb } = this.state;
    return (
      <div
        ref={this.ref}
        className={"sc-thumb" + (large ? " sc-thumb-lg" : "")}
        data-transition={x.isTransition}
        title={t("Play scene")}
        aria-label={t("Play scene")}
        {...pressable(onPlay)}
        style={large ? { flex: "none", width: "96px", height: "54px", borderRadius: "4px" } : { height: "36px" }}
      >
        {thumb && (thumb.tile ? <TileImg thumb={thumb} /> : <img src={thumb.src} alt="" />)}
        <span className="sc-play">
          <PlayArrowIcon />
        </span>
        {large && <span className="sc-dur">{fmt(x.e - x.s)}</span>}
      </div>
    );
  }
}

function TransitionBadge({ t }) {
  return <span className="sc-badge sc-trans">{t("transition")}</span>;
}

// collapsed scene row
export class SceneRow extends React.PureComponent {
  select = () => this.props.onSelect(this.props.x.i);
  play = (e) => {
    e.stopPropagation();
    this.props.onPlay(this.props.x.i);
  };

  render() {
    const { t, x, q, hits, dim } = this.props;
    return (
      <div className="sc-item" data-i={x.i} {...pressable(this.select)} style={{ opacity: dim ? 0.4 : 1 }}>
        <Thumb t={t} x={x} projectId={this.props.projectId} folder={this.props.folder} onPlay={this.play} />
        <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "1px" }}>
          <div className="sc-meta">
            <span>
              {t("Scene")} {x.i + 1} · {rangeLabel(x.s, x.e)}
            </span>
            {x.isTransition && <TransitionBadge t={t} />}
            {x.tags.map((g) => (
              <span key={g} className="sc-badge sc-tag">
                {t(tagLabel(g))}
              </span>
            ))}
          </div>
          <div className="sc-ellipsis" style={{ font: "600 14px/19px var(--font-ui)", letterSpacing: "-0.2px", color: "var(--p-ink)" }}>
            <Hl text={x.title || t("Untitled scene")} q={q} />
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {hits > 0 && <span className="sc-badge sc-hits">{matchLabel(t, hits)}</span>}
          <span style={{ font: "400 12px/1 var(--font-mono)", color: "var(--p-muted)" }}>{fmt(x.e - x.s)}</span>
        </div>
      </div>
    );
  }
}

const CAST_STYLE = {
  central: { background: "var(--p-tint)", borderColor: "var(--p-accline)", fontWeight: 600 },
  involved: {},
  background: { background: "var(--p-surface)", fontStyle: "italic", color: "var(--p-muted)" },
};

// expanded scene card
export class SceneCard extends React.PureComponent {
  select = () => this.props.onSelect(this.props.x.i);
  play = () => this.props.onPlay(this.props.x.i);
  collapse = () => this.props.onCollapse(this.props.x.i);
  toggleTr = () => this.props.onToggleTr(this.props.x.i);
  seek = (time) => () => this.props.onSelect(this.props.x.i, time);

  render() {
    const { t, x, q, sel, dim, arcs, arc, who, trOpen } = this.props;
    const now = sel ? this.props.currentTime : -1;
    let activeLine = -1;
    if (sel) x.lines.forEach((l, k) => l.t <= now + 0.01 && (activeLine = k));
    const others = x.others.length ? "+ " + x.others.join(", ") : "";
    const lineHits = countLineMatches(x, q);

    return (
      <div className="sc-open" data-i={x.i} data-sel={sel} style={{ opacity: dim ? 0.4 : 1 }}>
        <div style={{ padding: "12px 16px", display: "flex", gap: "14px", alignItems: "flex-start", borderBottom: "1px solid var(--p-line2)" }}>
          <Thumb t={t} x={x} projectId={this.props.projectId} folder={this.props.folder} large onPlay={this.play} />
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
            <div {...pressable(this.select)} style={{ font: "700 19px/26px var(--font-ui)", letterSpacing: "-0.3px", color: "var(--p-accent)", textWrap: "pretty", cursor: "pointer" }}>
              <Hl text={x.title || t("Untitled scene")} q={q} />
            </div>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px 8px", font: "400 13px/18px var(--font-mono)", color: "var(--p-muted)" }}>
              <span>
                {t("Scene")} {x.i + 1} · {rangeLabel(x.s, x.e)} · {fmt(x.e - x.s)}
                {/* shots[0]-shots[1] are the video's shot indexes: show how many shots the scene has */}
                {x.shots.length === 2 &&
                  x.shots[1] >= x.shots[0] &&
                  " · " + (x.shots[1] === x.shots[0] ? t("1 shot") : t("{{count}} shots", { count: x.shots[1] - x.shots[0] + 1 }))}
              </span>
              {x.isTransition && <TransitionBadge t={t} />}
            </div>
            {x.setting && (
              <div style={{ display: "flex", gap: "4px", alignItems: "flex-start", font: "400 13px/18px var(--font-ui)", color: "var(--p-muted)", marginTop: "2px" }}>
                <PlaceIcon style={{ fontSize: "14px", color: "var(--p-icon)", marginTop: "2px" }} />
                <span>
                  <Hl text={x.setting} q={q} />
                </span>
              </div>
            )}
            {x.tags.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "4px" }}>
                {x.tags.map((g) => (
                  <span key={g} className="sc-badge sc-tag">
                    {t(tagLabel(g))}
                  </span>
                ))}
              </div>
            )}
          </div>
          <span className="sc-close sc-collapse" title={t("Collapse")} aria-label={t("Collapse")} {...pressable(this.collapse)}>
            <ExpandLessIcon />
          </span>
        </div>

        {(x.summary || x.arcs.length > 0 || x.characters.length > 0 || others) && (
          <div className="sc-sec" style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: "10px" }}>
            {x.summary && (
              <div className="sc-prose">
                <Hl text={x.summary} q={q} />
              </div>
            )}
            {x.arcs.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                {x.arcs.map((sa) => {
                  const a = arcs[sa.k],
                    w = weightColors(a.weight);
                  return (
                    <span
                      key={sa.k}
                      className="sc-arcchip"
                      title={sa.note}
                      {...pressable(() => this.props.onArc(sa.k))}
                      style={{ boxShadow: arc === sa.k ? "0 0 0 2px var(--p-accent)" : "none" }}
                    >
                      <span className="sc-weight" style={{ width: "14px", height: "14px", background: w[0], color: w[1], fontSize: "9px", lineHeight: "14px" }}>
                        {a.label}
                      </span>
                      <span className="sc-ellipsis" style={{ fontWeight: 600 }}>
                        {a.title}
                      </span>
                      <span style={{ flex: "none", color: "var(--p-muted)" }}>· {t(ARC_ROLE[sa.role] || sa.role)}</span>
                    </span>
                  );
                })}
              </div>
            )}
            {(x.characters.length > 0 || others) && (
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px" }}>
                {x.characters.map((c) => (
                  <span
                    key={c.name}
                    className="sc-chip"
                    title={t(ROLE[c.role] || c.role)}
                    {...pressable(() => this.props.onWho(c.name))}
                    style={{ ...(CAST_STYLE[c.role] || {}), boxShadow: who === c.name ? "0 0 0 2px var(--p-accent)" : "none" }}
                  >
                    <Hl text={c.name} q={q} />
                  </span>
                ))}
                {others && <span style={{ marginLeft: "2px", font: "400 13px/19px var(--font-ui)", color: "var(--p-muted)" }}>{others}</span>}
              </div>
            )}
          </div>
        )}

        {x.moments.length > 0 && (
          <div className="sc-sec" style={{ padding: "8px 16px" }}>
            <div className="sc-overline" style={{ marginBottom: "2px" }}>
              {t("Moments")}
            </div>
            {x.moments.map((m, k) => (
              <div key={k} className="sc-moment" data-on={now >= m.s - 0.01 && now < m.e} {...pressable(this.seek(m.s))}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span style={{ font: "600 12px/18px var(--font-ui)", color: "var(--p-ink)" }}>{t("Moment {{count}}", { count: k + 1 })}</span>
                  <span className="sc-time">{rangeLabel(m.s, m.e)}</span>
                </div>
                <span className="sc-prose">
                  <Hl text={m.text} q={q} />
                </span>
              </div>
            ))}
          </div>
        )}

        {x.beats.length > 0 && (
          <div className="sc-sec" style={{ padding: "10px 16px 8px" }}>
            <div className="sc-overline" style={{ marginBottom: "2px" }}>
              {t("Character beats")}
            </div>
            {x.beats.map((b, k) => (
              <div key={k} style={{ display: "grid", gridTemplateColumns: "104px minmax(0,1fr)", gap: "12px", padding: "4px 0", alignItems: "baseline" }}>
                <span className="sc-time" title={t("Play from here")} {...pressable(this.seek(b.s))} style={{ fontSize: "13px", lineHeight: "20px", cursor: "pointer" }}>
                  {rangeLabel(b.s, b.e)}
                </span>
                <span className="sc-small">
                  <Hl text={b.text} q={q} />
                </span>
              </div>
            ))}
          </div>
        )}

        {x.outcome && (
          <div className="sc-sec">
            <div className="sc-overline" style={{ marginBottom: "2px" }}>
              {t("Outcome")}
            </div>
            <div className="sc-small">
              <Hl text={x.outcome} q={q} />
            </div>
          </div>
        )}

        {x.facts.length > 0 && (
          <div className="sc-sec">
            <div className="sc-overline" style={{ marginBottom: "4px" }}>
              {t("Facts")}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {x.facts.map((f, k) => (
                <div key={k} style={{ display: "flex", gap: "8px", alignItems: "baseline" }}>
                  <span className="sc-kind">{t(f.kind)}</span>
                  <span className="sc-small">
                    <Hl text={f.text} q={q} />
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {x.lines.length > 0 && (
          <div className="sc-sec" style={{ padding: "6px 16px 10px" }}>
            <div className="sc-toggle" {...pressable(this.toggleTr, { "aria-expanded": !!trOpen })}>
              {trOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              <span>{t("Transcript")}</span>
              <span style={{ font: "400 12px/18px var(--font-mono)", color: "var(--p-muted)" }}>
                {x.lines.length === 1 ? t("1 line") : t("{{count}} lines", { count: x.lines.length })}
                {lineHits > 0 && " · " + matchLabel(t, lineHits)}
              </span>
            </div>
            {trOpen && (
              <div style={{ marginTop: "4px", borderTop: "1px solid var(--p-line2)" }}>
                {x.lines.map((l, k) => (
                  <div key={k} className="sc-line" data-on={k === activeLine} {...pressable(this.seek(l.t))}>
                    <span>{fmt(l.t)}</span>
                    <span title={l.speaker}>
                      <Hl text={l.speaker} q={q} />
                    </span>
                    <span>
                      <Hl text={l.text} q={q} />
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}
